import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Button, List, Typography } from 'antd';
import { DeleteOutlined, UserOutlined } from '@ant-design/icons';
import CountdownPill from '../../../Shared/CountdownPill';
import TypingIndicator from '../../../Shared/TypingIndicator';
import { getConversationCountdownEndTime, getRemainingCountdownSeconds } from '../countdownUtils';

const { Text } = Typography;

const ContactSidebarItem = ({
    contact,
    conversation,
    isActive,
    isMobile,
    isDarkMode,
    t,
    listThemeColors,
    bgColor,
    onSelect,
    hoveredContactId,
    setHoveredContactId,
    onDelete
}) => {
    const [secondsUntilProcessing, setSecondsUntilProcessing] = useState(0);

    const countdownEndsAtRef = useRef(0);
    const countdownVersionRef = useRef(0);
    const [countdownVersion, setCountdownVersion] = useState(0);

    const isWriting = Boolean(conversation?.is_writing);

    const previewText = useMemo(() => {
        const last_message = conversation?.last_message;
        const hasContent = Boolean(last_message?.content);
        const hasMedias = Array.isArray(last_message?.medias) && last_message.medias.length > 0;
        return !last_message
            ? t('no_messages_yet')
            : (!hasContent && !hasMedias && last_message.sender_type === 'agent')
                ? t('agent_declined_to_respond')
                : (!hasContent && hasMedias)
                    ? t('media_message')
                    : (last_message.content || t('no_messages_yet'));
    }, [conversation?.last_message, t]);

    // Sync countdown with conversation updates by anchoring to a real end timestamp.
    useEffect(() => {
        const endTime = getConversationCountdownEndTime(conversation);
        const remaining = getRemainingCountdownSeconds(endTime);

        if (!remaining) {
            countdownEndsAtRef.current = 0;
            setSecondsUntilProcessing(0);
            return;
        }

        countdownEndsAtRef.current = endTime;
        setSecondsUntilProcessing(remaining);
        countdownVersionRef.current += 1;
        setCountdownVersion(countdownVersionRef.current);
    }, [
        conversation?.id,
        conversation?.processing_delay_applied_at,
        conversation?.processing_delay_applied_seconds,
        conversation?.processing_starts_in
    ]);

    useEffect(() => {
        if (!countdownEndsAtRef.current) return;
        const id = setInterval(() => {
            const next = getRemainingCountdownSeconds(countdownEndsAtRef.current);
            setSecondsUntilProcessing((prev) => (prev === next ? prev : next));
            if (next <= 0) {
                countdownEndsAtRef.current = 0;
                clearInterval(id);
            }
        }, 250);
        return () => clearInterval(id);
    }, [countdownVersion]);

    const name = `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim() || t('virtual_contact');

    return (
        <List.Item
            onClick={onSelect}
            onMouseEnter={() => setHoveredContactId(contact?.id)}
            onMouseLeave={() => setHoveredContactId(null)}
            style={{
                cursor: 'pointer',
                padding: isMobile ? '10px 14px' : '8px 16px',
                background: bgColor,
                transition: 'background-color 0.2s ease',
                position: 'relative'
            }}
        >
            <List.Item.Meta
                style={{ alignItems: 'center', margin: 0 }}
                avatar={
                    <Avatar
                        src={contact?.picture_url}
                        icon={!contact?.picture_url && <UserOutlined />}
                        size={isMobile ? 36 : 40}
                    />
                }
                title={<span style={{ color: listThemeColors.text, fontSize: isMobile ? 14 : undefined }}>{name}</span>}
                description={
                    <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, marginTop: 2 }}>
                        {secondsUntilProcessing > 0 ? (
                            <>
                                <Text
                                    ellipsis
                                    style={{
                                        color: listThemeColors.secondaryText,
                                        fontSize: 12,
                                        flex: '0 1 auto',
                                        minWidth: 0,
                                    }}
                                >
                                    {previewText}
                                </Text>
                                <div style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6 }}>
                                    <CountdownPill
                                        seconds={secondsUntilProcessing}
                                        isDarkMode={isDarkMode}
                                        size="sm"
                                    />
                                </div>
                            </>
                        ) : isWriting ? (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <TypingIndicator />
                                <Text type="secondary" style={{ fontSize: 12, color: '#1890ff' }}>
                                    {t('agent_is_typing')}
                                </Text>
                            </div>
                        ) : (
                            <>
                                <Text
                                    ellipsis
                                    style={{
                                        color: listThemeColors.secondaryText,
                                        fontSize: 12,
                                        flex: '0 1 auto',
                                        minWidth: 0,
                                    }}
                                >
                                    {previewText}
                                </Text>
                            </>
                        )}
                    </div>
                }
            />
            {/* Hover actions */}
            <div
                style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: isMobile ? 'flex' : (hoveredContactId === contact?.id ? 'flex' : 'none'),
                    gap: 4
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={onDelete}
                    title={t('delete')}
                />
            </div>
        </List.Item>
    );
};

export default ContactSidebarItem;













